/**
 * DAVMAN AI INTEGRATION
 * Este archivo es parte de la integración personalizada de IA para Davman Persa.
 * NO es parte del código original de Chatwoot.
 *
 * Store Vuex para gestionar sugerencias de IA
 */

import * as types from './types';
import {
  getSuggestion,
  useSuggestion,
  dismissSuggestion,
  checkHealth,
} from '../../../api/davmanAi';

/**
 * Estado inicial
 */
const state = {
  // Sugerencias por conversación: { [conversationId]: suggestion }
  suggestions: {},

  // Estado de carga por conversación
  loading: {},

  // Errores por conversación
  errors: {},

  // Estado del servicio
  serviceAvailable: true,

  // Timestamp del último check de salud
  lastHealthCheck: null,
};

/**
 * Getters
 */
const getters = {
  /**
   * Obtiene la sugerencia para una conversación
   */
  getSuggestion: (state) => (conversationId) => {
    return state.suggestions[conversationId] || null;
  },

  /**
   * Verifica si hay una sugerencia para una conversación
   */
  hasSuggestion: (state) => (conversationId) => {
    return !!state.suggestions[conversationId];
  },

  /**
   * Verifica si está cargando para una conversación
   */
  isLoading: (state) => (conversationId) => {
    return !!state.loading[conversationId];
  },

  /**
   * Obtiene el error para una conversación
   */
  getError: (state) => (conversationId) => {
    return state.errors[conversationId] || null;
  },

  /**
   * Verifica si el servicio está disponible
   */
  isServiceAvailable: (state) => {
    return state.serviceAvailable;
  },
};

/**
 * Actions
 */
const actions = {
  /**
   * Obtiene la sugerencia para una conversación
   */
  async fetchSuggestion({ commit }, conversationId) {
    commit(types.SET_LOADING, { conversationId, loading: true });
    commit(types.CLEAR_ERROR, conversationId);

    try {
      const response = await getSuggestion(conversationId);

      if (response.hasSuggestion && response.suggestion) {
        commit(types.SET_SUGGESTION, {
          conversationId,
          suggestion: response.suggestion,
        });
      } else {
        commit(types.CLEAR_SUGGESTION, conversationId);
      }

      commit(types.SET_SERVICE_AVAILABLE, true);
    } catch (error) {
      commit(types.SET_ERROR, {
        conversationId,
        error: error.message || 'Error fetching suggestion',
      });

      // Si el error es de red, marcar servicio como no disponible
      if (!error.response) {
        commit(types.SET_SERVICE_AVAILABLE, false);
      }
    } finally {
      commit(types.SET_LOADING, { conversationId, loading: false });
    }
  },

  /**
   * Marca la sugerencia como usada
   */
  async markAsUsed({ commit }, conversationId) {
    try {
      await useSuggestion(conversationId);
      commit(types.CLEAR_SUGGESTION, conversationId);
    } catch (error) {
      console.error('[Davman AI] Error marking suggestion as used:', error);
    }
  },

  /**
   * Descarta la sugerencia
   */
  async dismiss({ commit }, conversationId) {
    try {
      await dismissSuggestion(conversationId);
      commit(types.CLEAR_SUGGESTION, conversationId);
    } catch (error) {
      console.error('[Davman AI] Error dismissing suggestion:', error);
    }
  },

  /**
   * Limpia la sugerencia localmente (sin notificar al servidor)
   */
  clearSuggestion({ commit }, conversationId) {
    commit(types.CLEAR_SUGGESTION, conversationId);
  },

  /**
   * Verifica la salud del servicio
   */
  async checkServiceHealth({ commit }) {
    try {
      const isHealthy = await checkHealth();
      commit(types.SET_SERVICE_AVAILABLE, isHealthy);
      commit(types.SET_LAST_HEALTH_CHECK, Date.now());
    } catch {
      commit(types.SET_SERVICE_AVAILABLE, false);
    }
  },
};

/**
 * Mutations
 */
const mutations = {
  [types.SET_SUGGESTION](state, { conversationId, suggestion }) {
    state.suggestions = {
      ...state.suggestions,
      [conversationId]: suggestion,
    };
  },

  [types.CLEAR_SUGGESTION](state, conversationId) {
    const { [conversationId]: _, ...rest } = state.suggestions;
    state.suggestions = rest;
  },

  [types.SET_LOADING](state, { conversationId, loading }) {
    state.loading = {
      ...state.loading,
      [conversationId]: loading,
    };
  },

  [types.SET_ERROR](state, { conversationId, error }) {
    state.errors = {
      ...state.errors,
      [conversationId]: error,
    };
  },

  [types.CLEAR_ERROR](state, conversationId) {
    const { [conversationId]: _, ...rest } = state.errors;
    state.errors = rest;
  },

  [types.SET_SERVICE_AVAILABLE](state, available) {
    state.serviceAvailable = available;
  },

  [types.SET_LAST_HEALTH_CHECK](state, timestamp) {
    state.lastHealthCheck = timestamp;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
